
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { auth_axios } from "../../../libs/authWeb"

import DiscoverActionPanel from "./DiscoverActionPanel"
import DiscoverBulkStatusPanel from "./DiscoverBulkStatusPanel"
import DiscoverCollapsibleSection from "./DiscoverCollapsibleSection"
import DiscoverFilters from "./DiscoverFilters"
import DiscoverResults from "./DiscoverResults"
import DiscoverTabs from "./DiscoverTabs"
import {
  discover_status_url,
  discover_bulk_fetch_url,
  discover_list_url,
  discover_search_url,
  discover_single_url,
  discover_provider_options,
  discover_entity_options,
  get_default_action_config,
  get_tab_action_config,
  normalize_discover_data,
  get_unique_tags,
  build_initial_tab_filters,
} from "./discover_helpers"

const bulk_axios_timeout_ms = 900000

const DiscoverPage = () => {
  const [active_tab, set_active_tab] = useState("kalshi_markets")

  const [bulk_buckets, set_bulk_buckets] = useState({})
  const [is_bulk_running, set_is_bulk_running] = useState(false)
  const [bulk_run_bucket, set_bulk_run_bucket] = useState(null)
  const [bulk_error_text, set_bulk_error_text] = useState("")

  const [action_config, set_action_config] = useState(get_default_action_config())
  const [single_items, set_single_items] = useState([])
  const [single_error_text, set_single_error_text] = useState("")
  const [is_single_loading, set_is_single_loading] = useState(false)

  const [tab_draft_filters, set_tab_draft_filters] = useState(() => build_initial_tab_filters())
  const [tab_applied_filters, set_tab_applied_filters] = useState(() => build_initial_tab_filters())

  const [tab_raw_items, set_tab_raw_items] = useState([])
  const [tab_window_count, set_tab_window_count] = useState(0)
  const [tab_total_count, set_tab_total_count] = useState(0)
  const [tab_error_text, set_tab_error_text] = useState("")
  const [tab_loading, set_tab_loading] = useState(false)

  const tab_filter_snapshot_ref = useRef({
    active_tab: "kalshi_markets",
    tab_applied_filters: build_initial_tab_filters(),
  })

  tab_filter_snapshot_ref.current = {
    active_tab: active_tab,
    tab_applied_filters: tab_applied_filters,
  }

  const tab_action_config = useMemo(() => {
    return get_tab_action_config(active_tab)
  }, [active_tab])

  const normalized_tab_rows = useMemo(() => {
    return normalize_discover_data({ items: tab_raw_items }, tab_action_config)
  }, [tab_raw_items, tab_action_config])

  const unique_tag_options = useMemo(() => {
    return get_unique_tags(normalized_tab_rows)
  }, [normalized_tab_rows])

  const draft_for_active_tab = tab_draft_filters[active_tab]
  const applied_for_active_tab = tab_applied_filters[active_tab]

  const filters_stale =
    draft_for_active_tab.search.trim() !== applied_for_active_tab.search.trim()
    || draft_for_active_tab.tag !== applied_for_active_tab.tag

  const set_draft_search_for_active_tab = (value) => {
    set_tab_draft_filters((prev) => ({
      ...prev,
      [active_tab]: {
        ...prev[active_tab],
        search: value,
      },
    }))
  }

  const set_draft_tag_for_active_tab = (value) => {
    set_tab_draft_filters((prev) => ({
      ...prev,
      [active_tab]: {
        ...prev[active_tab],
        tag: value,
      },
    }))
  }

  const load_tab_from_backend = useCallback(async (bucket, applied_search, applied_tag) => {
    set_tab_loading(true)
    set_tab_error_text("")

    const has_filters =
      (applied_search || "").trim() !== ""
      || applied_tag !== "all"

    try {
      if (has_filters) {
        const res = await auth_axios.post(discover_search_url, {
          bucket: bucket,
          search_text: applied_search,
          tag_filter: applied_tag,
        })

        if (!res.data.success) {
          set_tab_error_text(res.data.status || "DISCOVER_SEARCH_FAILED")
          set_tab_raw_items([])
          set_tab_window_count(0)
          set_tab_total_count(0)
          return
        }

        const items = res.data.items || []
        set_tab_raw_items(items)
        set_tab_window_count(items.length)
        set_tab_total_count(typeof res.data.total_matched === "number" ? res.data.total_matched : 0)
      }
      else {
        const res = await auth_axios.get(discover_list_url(bucket))

        if (!res.data.success) {
          set_tab_error_text(res.data.status || "DISCOVER_LIST_FAILED")
          set_tab_raw_items([])
          set_tab_window_count(0)
          set_tab_total_count(0)
          return
        }

        const items = res.data.items || []
        set_tab_raw_items(items)
        set_tab_window_count(items.length)
        set_tab_total_count(typeof res.data.total_in_cache === "number" ? res.data.total_in_cache : 0)
      }
    }
    catch (error) {
      const msg =
        error?.response?.data?.status
        || error?.response?.data?.message
        || error?.message
        || "Tab load failed."
      set_tab_error_text(msg)
      set_tab_raw_items([])
      set_tab_window_count(0)
      set_tab_total_count(0)
    }
    finally {
      set_tab_loading(false)
    }
  }, [])

  const apply_tab_filters = () => {
    const draft = tab_draft_filters[active_tab]
    const next_applied = {
      search: draft.search.trim(),
      tag: draft.tag,
    }

    set_tab_applied_filters((prev) => ({
      ...prev,
      [active_tab]: next_applied,
    }))

    void load_tab_from_backend(active_tab, next_applied.search, next_applied.tag)
  }

  const load_status = useCallback(async () => {
    try {
      const res = await auth_axios.get(discover_status_url)
      if (res.data.success) {
        set_bulk_buckets(res.data.buckets || {})
      }
    }
    catch (error) {
      console.log(error)
    }
  }, [])

  useEffect(() => {
    void load_status()
  }, [load_status])

  const discover_bulk_status_poll_active =
    is_bulk_running
    || Object.values(bulk_buckets).some((row) => {
      if (!row || typeof row !== "object") {
        return false
      }
      return (row as { fetching?: boolean }).fetching === true
    })

  useEffect(() => {
    if (!discover_bulk_status_poll_active) {
      return undefined
    }

    void load_status()

    const interval_id = window.setInterval(() => {
      void load_status()
    }, 2000)

    return () => window.clearInterval(interval_id)
  }, [discover_bulk_status_poll_active, load_status])

  useEffect(() => {
    const applied = tab_applied_filters[active_tab]
    void load_tab_from_backend(active_tab, applied.search, applied.tag)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- tab switch only; Apply calls load_tab_from_backend directly
  }, [active_tab, load_tab_from_backend])

  const reload_current_tab_from_snapshot = async () => {
    const snap = tab_filter_snapshot_ref.current
    const f = snap.tab_applied_filters[snap.active_tab]
    await load_tab_from_backend(snap.active_tab, f.search, f.tag)
  }

  const on_fetch_bucket = async (bucket) => {
    set_bulk_error_text("")
    set_is_bulk_running(true)
    set_bulk_run_bucket(bucket)

    try {
      await auth_axios.post(
        discover_bulk_fetch_url,
        { bucket: bucket },
        { timeout: bulk_axios_timeout_ms }
      )
      await load_status()
      await reload_current_tab_from_snapshot()
    }
    catch (error) {
      const msg =
        error?.response?.data?.status
        || error?.response?.data?.message
        || error?.message
        || "Bulk fetch failed."
      set_bulk_error_text(msg)
    }
    finally {
      set_is_bulk_running(false)
      set_bulk_run_bucket(null)
    }
  }

  const on_refresh_all = async () => {
    set_bulk_error_text("")
    set_is_bulk_running(true)
    set_bulk_run_bucket("all")

    try {
      await auth_axios.post(
        discover_bulk_fetch_url,
        { refresh_all: true },
        { timeout: bulk_axios_timeout_ms }
      )
      await load_status()
      await reload_current_tab_from_snapshot()
    }
    catch (error) {
      const msg =
        error?.response?.data?.status
        || error?.response?.data?.message
        || error?.message
        || "Refresh all failed."
      set_bulk_error_text(msg)
    }
    finally {
      set_is_bulk_running(false)
      set_bulk_run_bucket(null)
    }
  }

  const run_single_fetch = async () => {
    if (!action_config.identifier.trim()) {
      set_single_error_text(`Please enter a ${action_config.lookup} before running this fetch.`)
      return
    }

    set_is_single_loading(true)
    set_single_error_text("")

    try {
      const res = await auth_axios.post(discover_single_url, {
        provider: action_config.provider,
        entity: action_config.entity,
        lookup: action_config.lookup,
        identifier: action_config.identifier.trim(),
      })

      if (!res.data.success) {
        set_single_error_text(res.data.status || "DISCOVER_SINGLE_FAILED")
        set_single_items([])
        return
      }

      console.log(res)

      const normalized = normalize_discover_data(res.data.discover_data, action_config)
      set_single_items(normalized)
    }
    catch (error) {
      const msg =
        error?.response?.data?.status
        || error?.response?.data?.message
        || error?.message
        || "Single fetch failed."
      set_single_error_text(msg)
      set_single_items([])
    }
    finally {
      set_is_single_loading(false)
    }
  }

  const tab_empty_message =
    applied_for_active_tab.search.trim() === "" && applied_for_active_tab.tag === "all" && tab_total_count === 0
      ? "Nothing in Redis for this tab yet. Run the matching bulk fetch above, then results will show here."
      : "No rows matched your server search for this tab."

  return (
    <div className="px-4 py-8 pb-32 md:py-16 sm:px-6 md:flex-auto md:px-0 md:py-8">
      <div className="mx-auto max-w-2xl space-y-16 sm:space-y-20 md:mx-0 md:max-w-none">
        <div>
          <h2 className="text-base font-semibold leading-7 text-gray-900">Discover</h2>
          <p className="mt-1 text-sm leading-6 text-gray-500">
            Bulk lists live in Redis; single lookups hit Kalshi or Polymarket directly.
          </p>

          {bulk_error_text && (
            <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {bulk_error_text}
            </div>
          )}

          <DiscoverCollapsibleSection title="Single fetch (live API)" default_open={false}>
            <DiscoverActionPanel
              action_config={action_config}
              set_action_config={set_action_config}
              run_single_fetch={run_single_fetch}
              discover_provider_options={discover_provider_options}
              discover_entity_options={discover_entity_options}
              is_single_loading={is_single_loading}
            />
          </DiscoverCollapsibleSection>

          <DiscoverCollapsibleSection title="Single fetch results" default_open={false}>
            <DiscoverResults
              section_title={null}
              filtered_items={single_items}
              is_loading={is_single_loading}
              fetch_error_text={single_error_text}
              api_hint_for_errors={discover_single_url}
              empty_message="Run a single fetch above to see one market or event here."
            />
          </DiscoverCollapsibleSection>

          <DiscoverCollapsibleSection title="Bulk cache (Redis)" default_open={true}>
            <DiscoverBulkStatusPanel
              bulk_buckets={bulk_buckets}
              is_bulk_running={is_bulk_running}
              bulk_run_bucket={bulk_run_bucket}
              on_fetch_bucket={on_fetch_bucket}
              on_refresh_all={on_refresh_all}
              hide_inner_title={true}
            />
          </DiscoverCollapsibleSection>

          <DiscoverCollapsibleSection title="Browse cached markets & events" default_open={true}>
            <div className="space-y-4">
              <DiscoverTabs
                active_tab={active_tab}
                set_active_tab={set_active_tab}
              />

              <DiscoverFilters
                search_text={draft_for_active_tab.search}
                set_search_text={set_draft_search_for_active_tab}
                selected_tag_filter={draft_for_active_tab.tag}
                set_selected_tag_filter={set_draft_tag_for_active_tab}
                unique_tag_options={unique_tag_options}
                filtered_count={tab_window_count}
                total_count={tab_total_count}
                tab_loading={tab_loading}
                filters_stale={filters_stale}
                on_apply_search={apply_tab_filters}
              />

              <DiscoverResults
                section_title="Tab results (Redis, first 100 window)"
                filtered_items={normalized_tab_rows}
                is_loading={tab_loading}
                fetch_error_text={tab_error_text}
                api_hint_for_errors={`${discover_list_url(active_tab)} / ${discover_search_url}`}
                empty_message={tab_empty_message}
              />
            </div>
          </DiscoverCollapsibleSection>
        </div>
      </div>
    </div>
  )
}

export default DiscoverPage
